import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  TrendingUp,
  Home,
  DollarSign,
  MapPin,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: "all", label: "All", icon: null },
  { id: "market_update", label: "Market Updates", icon: TrendingUp },
  { id: "buyer_tip", label: "Buyer Tips", icon: Home },
  { id: "seller_tip", label: "Seller Tips", icon: DollarSign },
  { id: "neighborhood", label: "Neighborhoods", icon: MapPin },
];

const placeholderResources = [
  {
    id: "1",
    title: "SoCal Market Update: What You Need to Know",
    category: "market_update",
    excerpt:
      "The market is shifting. Here's what buyers and sellers should understand about current conditions in Southern California.",
    content:
      "Full article content here...",
    image_url:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
  },
  {
    id: "2",
    title: "5 Mistakes First-Time Buyers Make (And How to Avoid Them)",
    category: "buyer_tip",
    excerpt:
      "I've seen it all. Here are the most common mistakes first-time buyers make and exactly how to avoid them.",
    content:
      "Full article content here...",
    image_url:
      "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=800",
  },
  {
    id: "3",
    title: "The Truth About Pricing Your Home",
    category: "seller_tip",
    excerpt:
      "Your neighbor's opinion doesn't matter. Zillow estimates are garbage. Here's how to actually price your home right.",
    content:
      "Full article content here...",
    image_url:
      "https://images.unsplash.com/photo-1560520653-9e0e4c89eb11?w=800",
  },
  {
    id: "4",
    title: "Rancho Cucamonga: Neighborhood Guide",
    category: "neighborhood",
    excerpt:
      "Everything you need to know about living in Rancho Cucamonga — schools, commute times, market trends, and more.",
    content:
      "Full article content here...",
    image_url:
      "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800",
  },
  {
    id: "5",
    title: "Should You Buy Now or Wait?",
    category: "buyer_tip",
    excerpt:
      "The million-dollar question. Here's my honest take on timing the market (spoiler: you probably can't).",
    content:
      "Full article content here...",
    image_url:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
  },
  {
    id: "6",
    title: "Staging Your Home: What Actually Matters",
    category: "seller_tip",
    excerpt:
      "Forget the expensive staging consultants. Here's what actually moves the needle when selling your home.",
    content:
      "Full article content here...",
    image_url:
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800",
  },
];

export default function ResourcesPage() {
  const [resources, setResources] = useState(placeholderResources);
  const [activeCategory, setActiveCategory] = useState("all");

  const fetchResources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/resources`);
      if (response.data.length > 0) {
        setResources(response.data);
      }
    } catch (_err) {
      // Fallback to placeholder resources - expected when no DB data exists
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const filteredResources =
    activeCategory === "all"
      ? resources
      : resources.filter((r) => r.category === activeCategory);

  const getCategoryIcon = (category) => {
    const cat = categories.find((c) => c.id === category);
    return cat?.icon || TrendingUp;
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find((c) => c.id === category);
    return cat?.label || category;
  };

  return (
    <div className="page-transition" data-testid="resources-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl space-y-8">
            <div className="space-y-4">
              <div className="gold-line" />
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white">
                <span className="text-[#D4AF37]">Resources</span>
              </h1>
            </div>
            <p className="text-xl text-zinc-300 leading-relaxed">
              Straight talk about real estate. Market updates, buyer tips,
              seller strategies, and neighborhood insights — no fluff, just
              facts.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-[#0A0A0A] border-y border-white/10 sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap gap-4">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 text-sm font-semibold uppercase tracking-wider transition-all ${
                  activeCategory === cat.id
                    ? "bg-[#D4AF37] text-black"
                    : "bg-transparent border border-white/20 text-zinc-400 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                }`}
                data-testid={`category-filter-${cat.id}`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredResources.map((resource, index) => {
              const IconComponent = getCategoryIcon(resource.category);
              return (
                <article
                  key={resource.id}
                  className="resource-card card-dark overflow-hidden animate-fadeInUp"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`resource-card-${index}`}
                >
                  {resource.image_url && (
                    <div className="img-zoom aspect-video">
                      <img
                        src={resource.image_url}
                        alt={resource.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-wider">
                      <IconComponent className="w-4 h-4" />
                      <span>{getCategoryLabel(resource.category)}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white leading-tight">
                      {resource.title}
                    </h3>
                    <p className="text-zinc-400 text-sm leading-relaxed">
                      {resource.excerpt}
                    </p>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-[#D4AF37] font-semibold uppercase tracking-wider text-sm hover:text-[#F3E5AB]"
                    >
                      Read More
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-16">
              <p className="text-zinc-400">
                No resources found in this category.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
            Have a <span className="text-[#D4AF37]">Question?</span>
          </h2>
          <p className="text-zinc-400 text-lg">
            Don't see what you're looking for? Reach out — I'm happy to answer
            your real estate questions directly.
          </p>
          <Link to="/contact">
            <Button
              className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest px-8 py-6"
              data-testid="resources-contact-button"
            >
              Ask Raad
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
